import {classNames, React, useLocalStore, observer, useAsync, useEffect, computedFn} from '@api'
import {Tooltip2} from '@api'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {IconButton, Tooltip} from '@material-ui/core'
import { CollectionTargetsToolbarConfig, CollectionTargetToolbarConfig, CollectionTargetColumnConfig,onSiteError, StoresInstance} from '@stores'
import {PrimaryIconButton} from 'centric-ui-components'
import {action, observable, toJS, computed, runInAction} from 'mobx'
import * as css from './CollectionTargetsToolbar.css'
import {Collapse, Icon, EditableText, Button} from '@blueprintjs/core'
import {useContext} from 'react'

interface ICollectionTargetRow {
  _id: string
  collection: string
  collectionVersion: string
  targets: Array<{ id: string, target?: number, text?: string }>
  notes: string
  summary: string
}

interface ITargetData {
  editing?: boolean
  actual?: number,
  target?: number,
  text?: string,
  newValue?: any,
  dirty?: boolean
}

export class CollectionTargetsToolbarStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
    this.catalog.componentStores.targetsToolbar = this
  }

  index = 'vlp-merch-collection-target'
  NOTES: string = "notes"
  SUMMARY: string = "summary"
  @computed get config(): Partial<CollectionTargetsToolbarConfig> { return this.catalog.targetsToolbarConfig || {}}

  @observable expanded = true
  @observable editable = true

  @action toggleExpanded = () => this.expanded = !this.expanded
  @action toggleEdit = () => this.editable = !this.editable

  @observable data: { [id: string]: ITargetData } = {}

  @computed get aggFields() {
    const {config, catalog} = this
    return _.uniq(_.flatMap((config?.targets ?? []).map(t => [t.field, ...(t?.computed?.deps?.filter(f => catalog._fieldByName[f]) ?? [])])).filter(_.identity))
  }

  @computed get aggs() {
    const {config: {targets}, catalog} = this

    return targets.reduce((acc, {field, id, agg, computed}) => {
      const catalogFields = [field, ...(computed?.deps ?? [])].filter(f => _.identity(f) && catalog._fieldByName[f])
      if (!agg && _.isEmpty(catalogFields)) return acc

      return {
        ...acc,
        ...(agg ? {
          [id ?? field]: {
            [agg ?? 'value_count']: {field: field ?? '_id'}
          }
        } : {})
      }
    }, {})
  }

  @computed get catalogResults () {
    const {config, aggResults, aggFields} = this
    return Object.keys(aggResults).reduce((acc, r) => ({
      ...acc,
      [r]: aggResults[r].value
    }), {})
  }

  @computed get computedFields() {
    const {config, count, aggResults, catalogResults, hits} = this
    const computedFields = config.targets.filter(t => t?.computed?.compute || t?.computed?.reduce)

    let recompute = true,
      result = {...catalogResults},
      tries = 0

    if (!hits.length) return result

    while (recompute && tries < 5) {
      const nextResult = {
        ...result,
        ...computedFields.reduce((acc, {id, computed: {deps, reduce, compute}}) => {
          _.set(acc, id, compute ? compute(result ?? {}) : reduce(hits ?? []))
          return acc
        }, {})
      }

      recompute = !_.isEqual(result, nextResult)
      //console.log({recompute, result, nextResult})
      result = nextResult
      tries++
    }

    return result
  }

  //'attributes.GNT_MPV_PlannedTargetVolume_double', 'attributes.GNT_MPV__PlannedTargetRRP_double', 'style.colorways.exId', 'style.colorways.attributes.Active', 'style.colorways.attributes.GNT_Colorway_TargetFOBPrice_Double'

  @observable count : number = null
  @observable.ref hits: Array<{_id} & any> = []
  @observable.ref aggResults : { [id: string]: {value: any}} = {}

  @computed get actuals() {
    const {config: {targets}, count, aggResults, computedFields} = this
    return (targets ?? []).reduce((acc, {id, agg, computed}) => {
        return {
          ...acc,
          [id]: {actual: agg == 'value_count' ? count : computed ? computedFields[id] : aggResults[id]?.value}
        }
      }, {})
  }

  @action loadActuals = async () => {
    const {es, config, aggFields, aggs, catalog} = this

    //console.log(aggFields)

    const body = {
      size: catalog.itemCount || 0,
      query: _.merge({}, catalog.catalogItemFilter.filter?.query ?? {}, {bool: (config.filter ?? {})}),
      _source: ['_id', 'exId', ...aggFields],
      aggs
    }

    const result = await es.search({
        meta: 'CollectionTargetsToolbarStore.loadData',
        index: catalog.index,
        body
      }, {log: {query: false, result: false}}),
      {total: {value: count}, items, aggregations} = es.unwrapHitsResult<any>(
        result)

    runInAction(() => {
      this.count = count
      this.hits = items
      this.aggResults = aggregations
      this.data = _.merge({}, _.cloneDeep(this.data), _.cloneDeep(this.actuals))
    })

    //console.log({count, items, computedFields: toJS(this.computedFields), actuals: toJS(this.actuals)})
  }

  @action loadActuals_gant = async () => {
    const {catalog, es} = this
    const result = await es.search({
        meta: 'CollectionTargetsToolbarStore.loadActuals',
        body: {
          size: catalog.itemCount,
          ...catalog.catalogItemFilter.filter,
          _source: ['_id', 'exId', ...this.aggFields]
        }
      }, {log: {query: false}}),
      {items} = es.unwrapHitsResult<{
        activeColorway?: { exId: string, attributes: { Active: boolean, GNT_Colorway_TargetFOBPrice_Double: number } },
        attributes: { GNT_MPV_PlannedTargetVolume_double: number, GNT_MPV__PlannedTargetRRP_double: number }, style?: { colorways?: Array<{ exId: string, attributes: { Active: boolean, GNT_Colorway_TargetFOBPrice_Double: number } }> }
      }>(
        result)

    // Get the first active colorway for each MPV
    items.forEach(item => {
      if (item?.style?.colorways?.length) {
        // Use the first active colorway if one exists
        item.activeColorway = item.style.colorways.find(c => c?.attributes?.Active)
      }
    })
    const activeItems = items.filter(v => v.activeColorway?.attributes?.GNT_Colorway_TargetFOBPrice_Double > 0 && v.attributes.GNT_MPV_PlannedTargetVolume_double > 0),
      profit = _.sum(
        activeItems
          .map(({
            activeColorway: {attributes: {GNT_Colorway_TargetFOBPrice_Double: cost}},
            attributes: {GNT_MPV__PlannedTargetRRP_double: price, GNT_MPV_PlannedTargetVolume_double: volume}
          }) => {
            return cost ? (price - cost) * volume : 0
          })),
      revenue = _.sum(activeItems.map(({attributes: {GNT_MPV__PlannedTargetRRP_double: cost, GNT_MPV_PlannedTargetVolume_double: volume}}) => cost * volume)),
      collectionMargin = !revenue ? 0 : profit / _.sum(activeItems.map(i => i.attributes.GNT_MPV_PlannedTargetVolume_double * i.attributes.GNT_MPV__PlannedTargetRRP_double)) * 100

    //console.log({mpvs: items, activeItems, profit, collectionMargin})

    this.data = _.merge(this.data, {
      products: {actual: items.length},
      volume: {actual: _.sum(activeItems.map(({attributes: {GNT_MPV_PlannedTargetVolume_double: volume}}) => volume))},
      revenue: {actual: revenue},
      margin: {actual: collectionMargin}
    })
  }

  @computed get collection() {
    const {vlp: {hierarchy: {selected}}} = this
    if (selected?.collections?.length === 1) {
      return selected.collections[0]
    }

    return null
  }

  @action loadTargets = async () => {
    const {es, filter: { state: { groups } } } = this,
      collectionVersion = _.first(Array.from(groups['collectionVersion'].selected.keys()).filter(_.identity)),
      {collection, data, index} = this

    if (!collection || !collectionVersion) {
      _.values(data).forEach(v => Object.assign(v, {target: null, text: null}))
      return
    }

    const hits = await es.search({
        meta:"Load Targets",
        index,
        body: {
          query: {bool: {filter: [{match: {collection}}, {match: {collectionVersion}}]}}
        }
      }, {log: {query: false}}),
      {items} = es.unwrapHitsResult<ICollectionTargetRow>(hits)

    //console.log({hits, items})

    runInAction(() => {
      const { NOTES, SUMMARY } = this
      _.values(data).forEach(v => Object.assign(v, {target: null, text: null}))
      if (items.length) {
        items[0].targets?.forEach(({id, target, text}) => {
          data[id] = _.merge(data[id], {target: null, text: null}, {target, text})
        })
      }
      data[NOTES] = items[0]?.notes ? {text: items[0].notes} : {text: null}
      data[SUMMARY] = items[0]?.summary ? {text: items[0].summary} : {text: null}
      this._id = items.length ? items[0]._id : null
    })
    //console.log(hits)
  }

  @observable _id = null

  @action loadData = async () => {
    const {loadTargets, loadActuals} = this

    await Promise.all([loadTargets(), loadActuals()])
  }

  tooltipFor = computedFn((config: CollectionTargetToolbarConfig, {actual, ...d}: ITargetData) => {
    let label = config.label ?? config.id,
      {labelForTarget} = this,
      target = d.dirty ? d.newValue : d.target,
      diff = target - actual

    if (diff > 0) {
      return `You need ${labelForTarget({...config, value: diff})} more to reach your target of ${labelForTarget({...config, value: target})}`
    } else if (diff === 0) {
      return `${_.capitalize(label)} exactly meets the target of ${target}`
    } else {
      diff = Math.abs(diff)
      return `You have exceeded your target of ${labelForTarget({...config, value: target})} by ${labelForTarget({...config, value: diff})}`
    }
  })

  labelForTarget = ({prefix, precision, value, suffix}: { value } & Partial<CollectionTargetToolbarConfig>) => {
    if (!value) return this.strings.emptyLabel
    return `${prefix ?? ''}${!value ? '' : precision ? value.toLocaleString(undefined, {maximumFractionDigits: precision}) : value.toLocaleString()}${suffix ?? ''}`
  }

  @action onBeginEdit = (id: string) => (value: string) => {
    const t = this.data[id]
    // t.newValue = value ? parseFloat(value.toString().replace(/\$/, '').replace(/\%/, '')) : 0
    // t.dirty = true
    t && (t.editing = true)
  }

  @action onConfirmEdit = (id: string) => (value: string) => {
    const t = this.data[id]
    if (t) {
      t.editing = false
    }
  }

  @action onChangeTarget = (id: string) => (value: string) => {
    const t = this.data[id]

    t.newValue = value ? parseFloat(value.replace(/\$/, '').replace(/\%/, '')) : null
    if (_.isNaN(t.newValue)) {
      t.newValue = null
    }

    t.dirty = t.newValue != t.target
  }

  @action onChangeText = (id: string) => (value: string) => {
    const t = this.data[id]
    if (t) {
      t.newValue = value
      t.dirty = t.newValue != t.text
    }
  }

  @action onCancelChange = (id: string) => (s: string) => {
    const t = this.data[id]
    t.newValue = null
    t.dirty = false
    t.editing = false
  }

  @computed get isDirty() {
    return _.values(this.data).some(t => t.dirty)
  }

  @action onCancelChanges = async () => {
    _.values(this.data).forEach(v => {
      if (v.dirty) {
        v.newValue = undefined
        v.dirty = false
      }
    })
  }

  @action onApplyChanges = async () => {
    await this.es.genericUpsert(this.upsertPayload)
    _.values(this.data).forEach(v => {
      if (v.dirty) {
        if (_.isNumber(v.newValue) || v.newValue === null) {
          v.target = v.newValue
        } else {
          v.text = v.newValue
        }

        v.dirty = false
      }
    })
  }

  @computed get upsertPayload() {
    const {vlp: {hierarchy: {selected}}, filter: { state: { groups } } } = this,
      collectionVersion = _.first(Array.from(groups['collectionVersion'].selected.keys()).filter(_.identity)),
      {collection, data, index, _id, NOTES, SUMMARY} = this
    return {
      index,
      _id,
      body: {
        collection,
        collectionVersion,
        notes: data?.notes && data?.notes.dirty ? data?.notes.newValue : data?.notes?.text,
        summary: data?.summary && data?.summary.dirty ? data?.summary.newValue : data?.summary?.text,
        targets: _.keys(_.omit(data, NOTES, SUMMARY)).map(id => {
          const {dirty, newValue, target, text} = data[id],
            isText = _.isString(newValue)

          return {
            id,
            target: !dirty || isText ? target : newValue,
            text: dirty && isText ? newValue : text
          }
        })
      }
    }
  }
}

export const CollectionTargetsToolbarStoreContext = React.createContext<CollectionTargetsToolbarStore>(undefined)
export const useCollectionTargetsToolbarStore = () => useContext(CollectionTargetsToolbarStoreContext)

export const CollectionTargetsToolbar = observer<React.HTMLProps<HTMLDivElement>>(({className, ...props}) => {
  const store = useVipLocalStore((stores) => new CollectionTargetsToolbarStore(stores)),
    {catalog: {es: {aggregateDetailsRefetchId, catalogItemFilter, refetchId}}, vlp: {hierarchy}} = store,
    {collection} = store

  const {error} = useAsync(
    async () => {
      if (hierarchy.collectionVersionFilter?.dataLoaded) {
        return store.loadData()
      }
    } ,
    [catalogItemFilter, collection, hierarchy.collectionVersionFilter?.dataLoaded, hierarchy.collectionVersionFilter?.groupedByValue, refetchId, aggregateDetailsRefetchId]
  )
  useEffect(() => onSiteError(error), [error])

  const {config: {columns}, expanded, toggleExpanded, isDirty, onCancelChanges, onApplyChanges} = store

  return (
    <CollectionTargetsToolbarStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)} {...props} data-expanded={expanded}>
        <div className={css.titlebar}>
          <PrimaryIconButton icon={<Icon icon={expanded ? 'chevron-down' : 'chevron-up'}/>} onClick={toggleExpanded}/>
          <span className={css.title}>Collection Details</span>

          {/*<PrimaryIconButton icon={<Icon icon={'edit'}/>}/>*/}
        </div>

        <Collapse isOpen={expanded} className={css.details}>
          <div className={css.contents}>
            {columns.map((c, i) => {
              const {type} = c
              switch (type) {
                case 'targets':
                  return <TargetsTableColumn key={i} column={c}/>

                case 'text':
                  return <TargetsTextColumn key={i} column={c}/>
              }
            })}
          </div>
        </Collapse>
        {isDirty && <div className={css.confirmToolbar}>
			<Button text='Cancel' onClick={onCancelChanges} small minimal={false}/>
			<Button intent='primary' text='Apply' onClick={onApplyChanges} small minimal={false}/>
		</div>}
      </div>
    </CollectionTargetsToolbarStoreContext.Provider>)
})

export const TargetsTableColumn = observer<{ column: CollectionTargetColumnConfig }>(({column: {label}}) => {
  const {strings, config, data, tooltipFor, labelForTarget, onChangeTarget, onCancelChange, onBeginEdit, onConfirmEdit} = useCollectionTargetsToolbarStore()
  return <div className={css.tableColumn}>
    {label && <label>{label}</label>}
    <table className={css.table}>
      <thead>
      <tr>
        <th></th>
        <th>Target</th>
        <th>Actual</th>
      </tr>
      </thead>
      <tbody>
      {config.targets.filter(t => !t.hidden).map(
        (t, i) => {
          const {id, label} = t, d = data[id],
            targetValue = d?.dirty ? d?.newValue : d?.target
          return (
            <tr key={i}>
              <td>{label ?? _.capitalize(id)}</td>
              <td>
                <EditableText
                  placeholder='--'
                  isEditing={i == 0}
                  className={css.editableText}
                  disabled={!d}
                  confirmOnEnterKey
                  selectAllOnFocus={true}
                  onEdit={onBeginEdit(id)}
                  onConfirm={onConfirmEdit(id)}
                  onChange={onChangeTarget(id)}
                  onCancel={onCancelChange(id)}
                  value={d?.editing ? null : targetValue ? labelForTarget({...t, value: targetValue}) : ''}
                />
              </td>
              <td data-name='actual'>
                <Tooltip2 content={d ? tooltipFor(t, d) : ''}>
                  <div className={css.actual}>
                    {<span className={css.indicator} data-reached={d?.actual ? d?.actual >= targetValue || !targetValue : undefined}/>}
                    {d?.actual === 0 ? strings.emptyLabel : d?.actual ? labelForTarget({...t, value: d.actual}) : '...'}
                  </div>
                </Tooltip2>
              </td>
            </tr>
          )
        }
      )}
      </tbody>
    </table>
  </div>
})

export const TargetsTextColumn = observer<{ column: CollectionTargetColumnConfig }>(({column: {label, field}}) => {
  const {data, onChangeText, onCancelChange, onBeginEdit, onConfirmEdit} = useCollectionTargetsToolbarStore()
  const t = data[field]
  return <div className={css.textColumn}>
      <span className={css.title}>
        <label>{label}</label>
        {/*<Tooltip2 content='Edit'><PrimaryIconButton icon={<Icon icon='edit'/>}/></Tooltip2>*/}
      </span>

    <div className={css.text}>
      <EditableText
        multiline
        selectAllOnFocus
        className={css.editableText}
        onEdit={onBeginEdit(field)}
        onConfirm={onConfirmEdit(field)}
        onChange={onChangeText(field)}
        onCancel={onCancelChange(field)}
        value={(t?.dirty ? t?.newValue : t?.text) ?? '...'}
      />
    </div>
  </div>
})




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/CollectionTargets/CollectionTargetsToolbar.tsx